/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Derive retrofit level and low-temp readiness from raw input
 * 
 * These derived metrics drive:
 * - Comfort recovery curves (how fast systems warm up)
 * - Heat pump suitability scoring
 * - Recommendation notes about emitters and insulation
 */

import type {
  SystemRecInput,
  EmitterProfile,
  RetrofitProfile,
  RetrofitLevel,
  LowTempReadiness,
} from './types';

/**
 * Normalize emitter input to EmitterProfile with safe defaults
 * Supports both new nested format (input.emitters) and legacy flat fields (input.heatEmitterType, etc.)
 */
export function normalizeEmitterProfile(input: SystemRecInput): EmitterProfile {
  const emitters = input.emitters || {};
  
  // Handle type: prefer nested format, fallback to legacy heatEmitterType
  let type: EmitterProfile['type'];
  if (emitters.type) {
    type = emitters.type;
  } else if (input.heatEmitterType) {
    // Map legacy heatEmitterType to new emitter type
    switch (input.heatEmitterType) {
      case "underfloor_heating":
        type = "underfloor";
        break;
      case "standard_rads":
      case "oversized_rads":
        type = "radiators";
        break;
      default:
        type = "unknown";
    }
  } else {
    type = "unknown";
  }
  
  // Handle lowTempSuitability: prefer nested format, fallback to radiatorSuitability
  let lowTempSuitability: EmitterProfile['lowTempSuitability'];
  if (emitters.lowTempSuitability) {
    lowTempSuitability = emitters.lowTempSuitability;
  } else if (input.radiatorSuitability) {
    // Map radiatorSuitability to lowTempSuitability
    switch (input.radiatorSuitability) {
      case "suitable":
        lowTempSuitability = "likely_ok";
        break;
      case "needs_upgrade":
        lowTempSuitability = "likely_needs_upgrade";
        break;
      default:
        lowTempSuitability = "unknown";
    }
  } else {
    lowTempSuitability = "unknown";
  }

  return {
    type,
    lowTempSuitability,
    roomsStruggleToHeat: emitters.roomsStruggleToHeat || "unknown",
    pipeBore: emitters.pipeBore || "unknown",
  };
}

/**
 * Normalize retrofit input to RetrofitProfile with safe defaults
 * Maps UI vocabulary (basic, modern, not_sure, etc.) to canonical engine values (some, good, unknown, etc.)
 * Supports both nested format (input.retrofit) and standalone fields (retrofitLoftInsulation, etc.)
 */
export function normalizeRetrofitProfile(input: SystemRecInput): RetrofitProfile {
  const retrofit = input.retrofit || {};
  
  // Access the input as 'any' to read unknown keys (standalone-web form fields)
  const inputAny = input as any;
  
  // For each retrofit field, prefer nested format, then fallback to standalone field
  const loftInsulation = retrofit.loftInsulation || inputAny.retrofitLoftInsulation;
  const wallInsulation = retrofit.wallInsulation || inputAny.retrofitWallInsulation;
  const windows = retrofit.windows || inputAny.retrofitWindows;
  const draughtProofing = retrofit.draughtProofing || inputAny.retrofitDraughtProofing;
  
  return {
    loftInsulation: normalizeOrdinalInsulation(loftInsulation),
    wallInsulation: normalizeWallInsulation(wallInsulation),
    windows: normalizeWindows(windows),
    draughtProofing: normalizeOrdinalInsulation(draughtProofing),
  };
}

/**
 * Normalize ordinal insulation values (loft/draught) from UI vocabulary to canonical engine values
 * Mapping: none→none, basic→some, modern→good, partial→some, not_sure→unknown
 * Used for both loft insulation and draught-proofing which share the same scale
 */
function normalizeOrdinalInsulation(
  value: string | undefined
): "unknown" | "none" | "some" | "good" {
  if (!value) return "unknown";
  
  switch (value) {
    case "none":
      return "none";
    case "basic":
    case "partial":
      return "some";
    case "modern":
    case "good":
      return "good";
    case "some":
      return "some";
    case "not_sure":
    case "unknown":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * Normalize wall insulation from UI vocabulary to canonical engine values
 * Mapping: none→none, cavity→cavity, solid_insulated→solid_or_internal_or_external, not_sure→unknown
 */
function normalizeWallInsulation(
  value: string | undefined
): "unknown" | "none" | "cavity" | "solid_or_internal_or_external" {
  if (!value) return "unknown";
  
  switch (value) {
    case "none":
      return "none";
    case "cavity":
      return "cavity";
    case "solid_insulated":
    case "solid_or_internal_or_external":
      return "solid_or_internal_or_external";
    case "solid_uninsulated":
      // Treat uninsulated solid walls as "none" in terms of insulation benefit
      return "none";
    case "not_sure":
    case "unknown":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * Normalize windows from UI vocabulary to canonical engine values
 * Mapping: single→single, double→older_double, modern→modern_double_or_triple, triple→modern_double_or_triple, not_sure→unknown
 */
function normalizeWindows(
  value: string | undefined
): "unknown" | "single" | "older_double" | "modern_double_or_triple" {
  if (!value) return "unknown";
  
  switch (value) {
    case "single":
      return "single";
    case "double":
    case "older_double":
      return "older_double";
    case "modern":
    case "modern_double_or_triple":
    case "triple":
      return "modern_double_or_triple";
    case "not_sure":
    case "unknown":
      return "unknown";
    default:
      return "unknown";
  }
}

/**
 * Derive retrofit level from retrofit profile
 * 
 * Algorithm:
 * - Start at 0 (neutral)
 * - Add points for good insulation indicators (×2 for integers)
 * - Subtract points for poor insulation indicators (×2 for integers)
 * 
 * Uses canonical engine vocabulary:
 * - "good" = high quality insulation
 * - "some" = medium quality insulation
 * - "solid_or_internal_or_external" = high quality wall insulation
 */
export function deriveRetrofitLevel(profile: RetrofitProfile): RetrofitLevel {
  let score = 0; // Start neutral
  
  // Positive indicators (bump up) - doubled to use integers
  if (profile.loftInsulation === "good") score += 2;
  if (profile.wallInsulation === "cavity" || profile.wallInsulation === "solid_or_internal_or_external") score += 2;
  if (profile.windows === "modern_double_or_triple") score += 2;
  if (profile.draughtProofing === "good") score += 2;
  
  // Negative indicators (bump down) - doubled to use integers
  if (profile.loftInsulation === "none") score -= 4;
  if (profile.wallInsulation === "none") score -= 4;
  if (profile.windows === "single") score -= 4;
  if (profile.draughtProofing === "none") score -= 2;
  
  // Partial positive - doubled to use integers
  if (profile.loftInsulation === "some") score += 1;
  if (profile.draughtProofing === "some") score += 1;
  if (profile.windows === "older_double") score += 1;
  
  // Map score to level (thresholds doubled)
  if (score >= 4) return "high";
  if (score <= -4) return "low";
  return "medium";
}

/**
 * Derive low-temp readiness from emitter profile and retrofit level
 * 
 * Algorithm:
 * - Base from emitter lowTempSuitability
 * - Bump up if underfloor heating present
 * - Bump down if rooms struggle to heat
 * - Bump down if retrofit level is low
 */
export function deriveLowTempReadiness(
  emitterProfile: EmitterProfile,
  retrofitLevel: RetrofitLevel
): LowTempReadiness {
  // Map base suitability to starting level
  let level: LowTempReadiness;

  if (emitterProfile.lowTempSuitability === "likely_ok") {
    level = "medium";
  } else if (emitterProfile.lowTempSuitability === "likely_needs_upgrade") {
    level = "low";
  } else if (emitterProfile.lowTempSuitability === "definitely_needs_upgrade") {
    // Definitely needs upgrade starts at low and will not be bumped up
    return "low";
  } else {
    // unknown -> assume medium
    level = "medium";
  }
  
  // Bump up if underfloor heating
  if (emitterProfile.type === "underfloor" || emitterProfile.type === "mixed") {
    if (level === "low") level = "medium";
    else if (level === "medium") level = "high";
  }
  
  // Bump down if rooms struggle
  if (emitterProfile.roomsStruggleToHeat === "yes") {
    if (level === "high") level = "medium";
    else if (level === "medium") level = "low";
  }
  
  // Bump down if retrofit is poor
  if (retrofitLevel === "low") {
    if (level === "high") level = "medium";
    else if (level === "medium") level = "low";
  }
  
  return level;
}

/**
 * All-in-one: derive all retrofit metrics from raw input
 */
export function deriveRetrofitMetrics(input: SystemRecInput) {
  const emitterProfile = normalizeEmitterProfile(input);
  const retrofitProfile = normalizeRetrofitProfile(input);
  const retrofitLevel = deriveRetrofitLevel(retrofitProfile);
  const lowTempReadiness = deriveLowTempReadiness(emitterProfile, retrofitLevel);
  
  return {
    emitterProfile,
    retrofitProfile,
    retrofitLevel,
    lowTempReadiness,
  };
}
